import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Waves,
  MapPin,
  Star,
  ArrowRight,
  Palmtree
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const SatelliteBeachPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for Satellite Beach homes. We understand beachside living—sand, salt air, and humidity management."
    },
    {
      title: "Deep Cleaning",
      description: "Thorough cleaning perfect for seasonal refreshes, getting salt buildup under control, or preparing for guests."
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Fast, reliable turnovers for Satellite Beach Airbnb and VRBO properties with guest-ready standards."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for beachside home transitions, helping renters and homeowners alike."
    }
  ];

  const faqs = [
    {
      question: "Do you serve all of Satellite Beach?",
      answer: "Yes. We serve all Satellite Beach neighborhoods—from Canova Beach to Pelican Beach, areas near Patrick Space Force Base, and homes along the A1A corridor. We know the unique cleaning challenges of living this close to the ocean."
    },
    {
      question: "How do you handle sand and salt air buildup?",
      answer: "It's part of beachside life. We focus extra attention on window tracks, screens, door thresholds, and any surfaces exposed to salt spray. Regular cleaning prevents the kind of buildup that can damage finishes over time."
    },
    {
      question: "Can you clean vacation rental properties in Satellite Beach?",
      answer: "Absolutely. We handle turnovers for Airbnb and VRBO properties throughout Satellite Beach. Fast, reliable, and guest-ready every time."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving Satellite Beach?",
      answer: "We're locally owned by Bob and Heather—not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we know how to protect your beachside investment."
    },
    {
      question: "Do you offer same-day service?",
      answer: "We do our best to accommodate urgent requests when possible. Call us at 321-408-1347 and we'll see what we can arrange."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Satellite Beach, FL | Pierce Premier",
      description: "Trusted beachside house cleaning in Satellite Beach, FL. Sand and salt air specialists. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/satellite-beach"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Satellite Beach" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Satellite Beach",
      description: "Professional beachside house cleaning in Satellite Beach, FL including recurring cleaning, deep cleaning, vacation rental turnover, and move-in/move-out services."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Satellite Beach, FL | Pierce Premier"
        description="Beachside house cleaning in Satellite Beach, FL. Sand & salt air specialists. Same team every visit, 15+ years experience, fully insured, vacation rentals welcome. Call 321-408-1347."
        canonical="/areas/satellite-beach"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  <Badge className="bg-teal-600 text-white">Beachside Living</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Satellite Beach, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Satellite Beach offers the best of beachside living—quiet neighborhoods, family-friendly beaches, and that small-town feel. Pierce Premier serves this community with cleaning services designed for homes that live close to the ocean.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're a year-round resident managing sand and salt air, or a vacation rental owner needing reliable turnovers, we bring the consistency and professionalism Satellite Beach families expect.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=800&q=80" 
                  alt="Beachside house cleaning in Satellite Beach, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Satellite Beach Homeowners Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Beachside homes need more than standard cleaning—they need someone who understands the challenges.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Waves className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">We Know Beachside Homes</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Sand, salt spray, humidity—living near the ocean presents unique cleaning challenges. Heather's 15+ years of experience includes plenty of beachside properties, so we know what works and what doesn't.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Trusted Team</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. You'll have the same cleaners every visit, so they learn your home, your preferences, and what matters most to you.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Palmtree className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Vacation Rental Ready</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Many Satellite Beach homeowners rent their properties on Airbnb or VRBO. We handle fast turnovers with guest-ready standards that protect your reviews.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fully Insured & Professional</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're fully licensed and insured, and we take responsibility for the work we do in your home.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Satellite Beach
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Satellite Beach has always felt like the kind of place where people actually know their neighbors. It's close to everything—Melbourne, the beaches, Patrick Space Force Base—but it keeps that small-town feel we appreciate.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Many of our Satellite Beach clients are families who've been here for years, or newcomers who fell in love with the area. Either way, we're glad to be part of keeping your home comfortable and clean so you can enjoy everything this community offers.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cleaning Services for Satellite Beach
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-teal-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Satellite Beach Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Canova Beach",
                "Pelican Beach",
                "Patrick SFB Area",
                "A1A Corridor",
                "Ocean Beach Blvd",
                "Cassia Blvd Area"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg border border-teal-100">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Code:</span> 32937
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Beachside Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Indian Harbour Beach", link: "/areas/indian-harbour-beach" },
                { name: "Indialantic", link: "/areas/indialantic" },
                { name: "Melbourne Beach", link: "#" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "Viera", link: "/areas/viera" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-teal-50 rounded-lg border-2 border-teal-200 hover:border-teal-400 transition-colors text-center group">
                    <span className="text-teal-700 font-semibold group-hover:text-teal-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-teal-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Satellite Beach Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Experience Beachside Cleaning Done Right?"
          description="Let Pierce Premier take care of your Satellite Beach home. Same trusted team, beachside expertise, and the quality you deserve."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default SatelliteBeachPage;